import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useClienteCalendario } from '../../../hooks/useClienteCalendario';
import { useAdminClientesAsesoria } from '../../../hooks/useAdminClientesAsesoria';
import { useAdminArmario } from '../../../hooks/useAdminArmario';
import { mockLooksCalendario, mockPrendas } from '../../../mocks/clienteArmario';
import type { LookCalendario } from '../../../hooks/useClienteCalendario';

const PAGE_SIZE = 10;
const ESTADOS: { value: LookCalendario['estado']; label: string; color: string }[] = [
  { value: 'planeado', label: 'Planificado', color: 'bg-brand-sage/20 text-brand-black' },
  { value: 'usado', label: 'Usado', color: 'bg-brand-black text-white' },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-brand-gray-200 text-brand-gray-500' },
];

interface Props {
  selectedClienteId?: number;
}

export default function AdminAsesoriaCalendario({ selectedClienteId }: Props) {
  const { data: clientesData } = useAdminClientesAsesoria();
  const [selectedCliente, setSelectedCliente] = useState<number>(selectedClienteId || 0);
  const [clientes, setClientes] = useState(clientesData);

  useEffect(() => {
    setClientes(clientesData);
    if (selectedClienteId && selectedClienteId > 0) {
      setSelectedCliente(selectedClienteId);
    } else if (clientesData.length > 0 && selectedCliente === 0) {
      setSelectedCliente(clientesData[0].id);
    }
  }, [clientesData, selectedClienteId, selectedCliente]);

  const looksCliente = mockLooksCalendario.filter((l) => l.cliente_id === selectedCliente);
  const prendasClienteMock = mockPrendas.filter((p) => p.cliente_id === selectedCliente);

  const {
    data,
    setData,
    refresh,
    loading,
  } = useClienteCalendario(looksCliente, selectedCliente || undefined);

  const { data: prendasDb } = useAdminArmario(prendasClienteMock, selectedCliente || undefined);
  const prendasDelCliente = prendasDb.length > 0 ? prendasDb : prendasClienteMock;

  const [editing, setEditing] = useState<LookCalendario | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState<string>('todas');
  const [formError, setFormError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [selectedPrendasIds, setSelectedPrendasIds] = useState<number[]>([]);

  useEffect(() => {
    if (editing) {
      setSelectedPrendasIds(editing.prendas_ids || []);
    } else if (isAdding) {
      setSelectedPrendasIds([]);
    }
  }, [editing, isAdding]);

  const currentCliente = clientes.find((c) => c.id === selectedCliente);

  const filtered = data.filter(
    (l) =>
      (estadoFilter === 'todas' || l.estado === estadoFilter) &&
      (l.titulo.toLowerCase().includes(search.toLowerCase()) ||
        (l.ocasion?.toLowerCase().includes(search.toLowerCase()) ?? false))
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paged = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const togglePrenda = (id: number) => {
    setSelectedPrendasIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAdd = async (formData: FormData) => {
    setFormError(null);
    const titulo = (formData.get('titulo') as string)?.trim();
    const fecha = (formData.get('fecha') as string);
    const notas = (formData.get('notas') as string)?.trim() || null;
    const ocasion = (formData.get('ocasion') as string)?.trim() || null;
    const estado = (formData.get('estado') as LookCalendario['estado']) || 'planeado';
    const prendas_ids = selectedPrendasIds;

    if (!titulo || !fecha) {
      setFormError('Título y fecha son obligatorios.');
      return;
    }

    setIsAdding(false);
    try {
      const { data: inserted, error: err } = await supabase
        .from('cliente_calendario_looks')
        .insert({ cliente_id: selectedCliente, titulo, fecha, notas, ocasion, estado, prendas_ids })
        .select()
        .single();
      if (err) {
        setFormError(err.message);
        setIsAdding(true);
        return;
      }
      if (inserted) {
        setData((prev) =>
          [...prev, inserted as LookCalendario].sort(
            (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
          )
        );
      }
    } catch {
      setFormError('Error al crear look');
      setIsAdding(true);
    }
  };

  const handleUpdate = async (formData: FormData) => {
    if (!editing) return;
    setFormError(null);
    const titulo = (formData.get('titulo') as string)?.trim();
    const fecha = (formData.get('fecha') as string);
    const notas = (formData.get('notas') as string)?.trim() || null;
    const ocasion = (formData.get('ocasion') as string)?.trim() || null;
    const estado = (formData.get('estado') as LookCalendario['estado']) || editing.estado;
    const prendas_ids = selectedPrendasIds;

    if (!titulo || !fecha) {
      setFormError('Título y fecha son obligatorios.');
      return;
    }

    const prevEditing = editing;
    setEditing(null);
    try {
      const { data: updated, error: err } = await supabase
        .from('cliente_calendario_looks')
        .update({ titulo, fecha, notas, ocasion, estado, prendas_ids })
        .eq('id', prevEditing.id)
        .select()
        .single();
      if (err) {
        setFormError(err.message);
        setEditing(prevEditing);
        return;
      }
      if (updated) {
        setData((prev) =>
          prev.map((item) => (item.id === prevEditing.id ? (updated as LookCalendario) : item))
        );
      }
    } catch {
      setFormError('Error al actualizar');
      setEditing(prevEditing);
    }
  };

  const handleRemove = async (id: number) => {
    if (!confirm('¿Eliminar este look del calendario?')) return;
    try {
      const { error: err } = await supabase
        .from('cliente_calendario_looks')
        .delete()
        .eq('id', id);
      if (err) {
        setFormError(err.message);
        return;
      }
      setData((prev) => prev.filter((item) => item.id !== id));
    } catch {
      setFormError('Error al eliminar');
    }
  };

  const moveItem = useCallback(
    async (fromId: number, toId: number) => {
      if (fromId === toId) return;
      const sorted = [...data].sort(
        (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
      );
      const fromIndex = sorted.findIndex((d) => d.id === fromId);
      const toIndex = sorted.findIndex((d) => d.id === toId);
      if (fromIndex === -1 || toIndex === -1) return;
      sorted.splice(fromIndex, 1);
      sorted.splice(toIndex, 0, data.find((d) => d.id === fromId)!);
      const reordered = sorted.map((item, idx) => ({
        ...item,
        sort_order: idx,
      }));
      setData(reordered);
    },
    [data, setData]
  );

  const handleDragStart = (
    e: React.DragEvent<HTMLTableRowElement>,
    id: number
  ) => {
    setDraggingId(id);
    e.dataTransfer.setData('text/plain', String(id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLTableRowElement>,
    id: number
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== id) setDragOverId(id);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLTableRowElement>,
    toId: number
  ) => {
    e.preventDefault();
    const fromId = Number(e.dataTransfer.getData('text/plain'));
    moveItem(fromId, toId);
    setDragOverId(null);
    setDraggingId(null);
  };

  const handleDragEnd = () => {
    setDragOverId(null);
    setDraggingId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header con selector de cliente */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <h2 className="font-serif text-brand-black text-2xl font-medium">
            Calendario de Looks
          </h2>
          <div className="relative">
            <select
              value={selectedCliente}
              onChange={(e) => {
                setSelectedCliente(Number(e.target.value));
                setPage(1);
              }}
              className="appearance-none bg-white border border-brand-gray-200 rounded-lg pl-4 pr-10 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black cursor-pointer"
            >
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-brand-gray-400">
              <i className="ri-arrow-down-s-line" />
            </div>
          </div>
          {currentCliente && (
            <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-sage/20 text-brand-black">
              {currentCliente.plan_contratado || 'Sin plan'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <select
            value={estadoFilter}
            onChange={(e) => {
              setEstadoFilter(e.target.value);
              setPage(1);
            }}
            className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          >
            <option value="todas">Todos los estados</option>
            {ESTADOS.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Buscar look..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-64 bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          />
          <button
            onClick={() => {
              setIsAdding(true);
              setEditing(null);
              setFormError(null);
            }}
            className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
          >
            + Nuevo look
          </button>
          <button
            onClick={refresh}
            className="w-10 h-10 rounded-lg bg-white border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:text-brand-black hover:border-brand-black transition-colors"
            title="Recargar"
          >
            <i className="ri-refresh-line" />
          </button>
        </div>
      </div>

      {currentCliente && (
        <div className="bg-brand-sage/10 border border-brand-sage/30 rounded-xl px-5 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-brand-sage/30 flex items-center justify-center text-brand-black">
            <i className="ri-user-line" />
          </div>
          <div>
            <p className="font-sans text-brand-black text-sm font-medium">{currentCliente.nombre}</p>
            <p className="font-sans text-brand-gray-600 text-xs">
              {data.length} looks planificados · {currentCliente.email}
            </p>
          </div>
        </div>
      )}

      {formError && !isAdding && !editing && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 mt-0.5" />
          <p className="font-sans text-red-600 text-sm">{formError}</p>
        </div>
      )}

      {/* Formulario */}
      {(isAdding || editing) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            if (editing) handleUpdate(fd);
            else handleAdd(fd);
          }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Look' : 'Nuevo Look'}
            </h3>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
                setFormError(null);
                setSelectedPrendasIds([]);
              }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 mt-0.5" />
              <p className="font-sans text-red-600 text-sm">{formError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Fecha *
              </label>
              <input
                name="fecha"
                type="date"
                defaultValue={editing?.fecha ?? new Date().toISOString().split('T')[0]}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Título *
              </label>
              <input
                name="titulo"
                defaultValue={editing?.titulo ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Estado
              </label>
              <select
                name="estado"
                defaultValue={editing?.estado ?? 'planeado'}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white"
              >
                {ESTADOS.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Ocasion
              </label>
              <input
                name="ocasion"
                defaultValue={editing?.ocasion ?? ''}
                placeholder="Ej: trabajo, social, evento..."
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Notas
              </label>
              <input
                name="notas"
                defaultValue={editing?.notas ?? ''}
                placeholder="Notas del look..."
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
          </div>

          {/* Picker visual de prendas */}
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Seleccionar prendas del armario
            </label>
            <p className="font-sans text-brand-gray-400 text-xs mt-1 mb-3">
              {selectedPrendasIds.length} prenda{selectedPrendasIds.length !== 1 ? 's' : ''} seleccionada{selectedPrendasIds.length !== 1 ? 's' : ''}
            </p>
            {prendasDelCliente.length === 0 ? (
              <div className="bg-brand-gray-50 rounded-xl p-4 text-center">
                <p className="font-sans text-brand-gray-400 text-sm">Este cliente aún no tiene prendas en el armario.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {prendasDelCliente.map((prenda) => {
                  const selected = selectedPrendasIds.includes(prenda.id);
                  return (
                    <button
                      key={prenda.id}
                      type="button"
                      onClick={() => togglePrenda(prenda.id)}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all text-left ${
                        selected
                          ? 'border-brand-black ring-1 ring-brand-black'
                          : 'border-transparent hover:border-brand-gray-300'
                      }`}
                    >
                      <div className="relative w-full aspect-[3/4] bg-brand-gray-50">
                        {prenda.imagen_url ? (
                          <img
                            src={prenda.imagen_url}
                            alt={prenda.nombre}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-brand-gray-300">
                            <i className="ri-t-shirt-line text-xl" />
                          </div>
                        )}
                        {selected && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-black text-white flex items-center justify-center">
                            <i className="ri-check-line text-xs" />
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="font-sans text-brand-black text-[11px] font-medium line-clamp-1">{prenda.nombre}</p>
                        <p className="font-sans text-brand-gray-400 text-[10px]">{prenda.categoria}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap disabled:opacity-60"
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
                setFormError(null);
                setSelectedPrendasIds([]);
              }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Tabla */}
      <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-gray-50">
              <tr>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3 w-10">
                  Orden
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Fecha
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Título
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Estado
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Prendas
                </th>
                <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-gray-200">
              {paged.map((look) => (
                <tr
                  key={look.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, look.id)}
                  onDragOver={(e) => handleDragOver(e, look.id)}
                  onDrop={(e) => handleDrop(e, look.id)}
                  onDragEnd={handleDragEnd}
                  className={`hover:bg-brand-gray-50/50 transition-colors cursor-move ${
                    draggingId === look.id ? 'opacity-40' : ''
                  } ${dragOverId === look.id ? 'bg-brand-sage/20' : ''}`}
                >
                  <td className="px-5 py-4">
                    <div className="w-6 h-6 flex items-center justify-center text-brand-gray-400">
                      <i className="ri-draggable text-lg" />
                    </div>
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">
                    {new Date(look.fecha).toLocaleDateString('es-ES', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                    })}
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-sans text-brand-black text-sm font-medium line-clamp-1 max-w-[260px]">
                      {look.titulo}
                    </p>
                    {look.ocasion && (
                      <p className="font-sans text-brand-gray-400 text-xs mt-0.5">
                        {look.ocasion}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium ${
                        ESTADOS.find((e) => e.value === look.estado)?.color || 'bg-brand-gray-100 text-brand-gray-500'
                      }`}
                    >
                      {ESTADOS.find((e) => e.value === look.estado)?.label || look.estado}
                    </span>
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">
                    <div className="flex items-center -space-x-2">
                      {look.prendas_ids.slice(0, 4).map((pid) => {
                        const prenda = prendasDelCliente.find((p) => p.id === pid);
                        return prenda?.imagen_url ? (
                          <img
                            key={pid}
                            src={prenda.imagen_url}
                            alt={prenda.nombre}
                            className="w-8 h-8 rounded-full object-cover border-2 border-white"
                            title={prenda.nombre}
                          />
                        ) : (
                          <div
                            key={pid}
                            className="w-8 h-8 rounded-full bg-brand-gray-100 flex items-center justify-center border-2 border-white"
                            title={prenda?.nombre ?? `Prenda #${pid}`}
                          >
                            <i className="ri-t-shirt-line text-brand-gray-400 text-xs" />
                          </div>
                        );
                      })}
                      {look.prendas_ids.length > 4 && (
                        <div className="w-8 h-8 rounded-full bg-brand-gray-100 flex items-center justify-center border-2 border-white font-sans text-[10px] text-brand-gray-500">
                          +{look.prendas_ids.length - 4}
                        </div>
                      )}
                      {look.prendas_ids.length === 0 && (
                        <span className="font-sans text-brand-gray-400 text-xs">Sin prendas</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditing(look);
                          setIsAdding(false);
                          setFormError(null);
                        }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Editar"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        onClick={() => handleRemove(look.id)}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-10 text-center">
            <p className="font-sans text-brand-gray-400 text-sm">
              No se encontraron looks
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-brand-gray-200 rounded-2xl px-5 py-3">
          <div className="font-sans text-brand-gray-500 text-sm">
            Mostrando {startIdx + 1}–
            {Math.min(startIdx + PAGE_SIZE, filtered.length)} de {filtered.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg font-sans text-sm flex items-center justify-center transition-colors ${
                  p === safePage
                    ? 'bg-brand-black text-white'
                    : 'border border-brand-gray-200 text-brand-gray-600 hover:border-brand-black hover:text-brand-black'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
